import { z } from 'zod';
import { MEETING_OCCURRENCE_STATUSES } from './meetings';

/**
 * Wave 5 enums: the calendar, emailed invitations, outside guests and the lobby, webinars and
 * booking pages (docs/DECISIONS.md D-1000 ff.; the wave 5 brief §5). The CHECK constraints of
 * packages/db/src/schema/{calendar-sharing,webinars,bookings,calls}.ts are built from these lists,
 * so a value added here needs a migration that widens the CHECK (core writes migrations).
 */

// ---------------------------------------------------------------- outside guests (D-1011, D-1032)

/** `meeting_guests.role`: `required`/`optional` on a general meeting, `presenter`/`attendee` on a webinar. */
export const MEETING_GUEST_ROLES = ['required', 'optional', 'presenter', 'attendee'] as const;
export type MeetingGuestRole = (typeof MEETING_GUEST_ROLES)[number];
export const meetingGuestRoleSchema = z.enum(MEETING_GUEST_ROLES);

/** The guest roles an organiser may give on a general meeting. */
export const GENERAL_GUEST_ROLES = [
  'required',
  'optional',
] as const satisfies readonly MeetingGuestRole[];
export const generalGuestRoleSchema = z.enum(GENERAL_GUEST_ROLES);

/** `meeting_guests.source`: added by the organiser, registered for a webinar, or booked a page. */
export const MEETING_GUEST_SOURCES = ['invited', 'registered', 'booked'] as const;
export type MeetingGuestSource = (typeof MEETING_GUEST_SOURCES)[number];
export const meetingGuestSourceSchema = z.enum(MEETING_GUEST_SOURCES);

/** `meeting_guests.status`. Removal never deletes the row (D-1034). */
export const MEETING_GUEST_STATUSES = ['active', 'removed'] as const;
export type MeetingGuestStatus = (typeof MEETING_GUEST_STATUSES)[number];
export const meetingGuestStatusSchema = z.enum(MEETING_GUEST_STATUSES);

/** `meeting_guests.registration_status` (webinar registrants only, D-1032). */
export const REGISTRATION_STATUSES = [
  'unconfirmed',
  'pending',
  'approved',
  'rejected',
  'waitlisted',
  'cancelled',
] as const;
export type RegistrationStatus = (typeof REGISTRATION_STATUSES)[number];
export const registrationStatusSchema = z.enum(REGISTRATION_STATUSES);

/** Registrations that hold a seat (the capacity count, D-1032). */
export const SEAT_HOLDING_REGISTRATION_STATUSES = [
  'approved',
  'unconfirmed',
] as const satisfies readonly RegistrationStatus[];

// ---------------------------------------------------------------- answers (D-1014)

/** `meeting_responses.response`. */
export const MEETING_RESPONSES = ['accepted', 'tentative', 'declined'] as const;
export type MeetingResponse = (typeof MEETING_RESPONSES)[number];
export const meetingResponseSchema = z.enum(MEETING_RESPONSES);

/** The effective answer: the occurrence row, else the series row, else `needs_action`. */
export const EFFECTIVE_MEETING_RESPONSES = [...MEETING_RESPONSES, 'needs_action'] as const;
export type EffectiveMeetingResponse = (typeof EFFECTIVE_MEETING_RESPONSES)[number];
export const effectiveMeetingResponseSchema = z.enum(EFFECTIVE_MEETING_RESPONSES);

/** `meeting_responses.source`. */
export const MEETING_RESPONSE_SOURCES = ['app', 'guest_page', 'booking', 'registration'] as const;
export type MeetingResponseSource = (typeof MEETING_RESPONSE_SOURCES)[number];
export const meetingResponseSourceSchema = z.enum(MEETING_RESPONSE_SOURCES);

// ---------------------------------------------------------------- invitation ledger (D-1016 ff.)

/** `meeting_invite_deliveries.mode` (D-1017): one series object, one per occurrence, or the chair's copy. */
export const INVITE_DELIVERY_MODES = ['series', 'occurrence', 'organiser_copy'] as const;
export type InviteDeliveryMode = (typeof INVITE_DELIVERY_MODES)[number];
export const inviteDeliveryModeSchema = z.enum(INVITE_DELIVERY_MODES);

/** `meeting_invite_deliveries.recipient_kind`. */
export const INVITE_RECIPIENT_KINDS = ['guest', 'member'] as const;
export type InviteRecipientKind = (typeof INVITE_RECIPIENT_KINDS)[number];
export const inviteRecipientKindSchema = z.enum(INVITE_RECIPIENT_KINDS);

/** iTIP methods a delivery exposes (`last_method`, `meeting_invite_messages.method`). */
export const INVITE_METHODS = ['REQUEST', 'CANCEL'] as const;
export type InviteMethod = (typeof INVITE_METHODS)[number];
export const inviteMethodSchema = z.enum(INVITE_METHODS);

/** Every METHOD an ICS object of the Hub may carry (downloads by participants are PUBLISH). */
export const ICS_METHODS = ['REQUEST', 'CANCEL', 'PUBLISH'] as const;
export type IcsMethod = (typeof ICS_METHODS)[number];
export const icsMethodSchema = z.enum(ICS_METHODS);

/** `meeting_invite_deliveries.last_exposure`: how the object at `last_sequence` reached the person. */
export const INVITE_EXPOSURES = ['email', 'manual_download', 'self_download'] as const;
export type InviteExposure = (typeof INVITE_EXPOSURES)[number];
export const inviteExposureSchema = z.enum(INVITE_EXPOSURES);

/** `meeting_invite_deliveries.status` (D-1018, D-1021). */
export const INVITE_DELIVERY_STATUSES = [
  'pending_mail',
  'sent',
  'manual',
  'stale',
  'failed',
  'suppressed',
] as const;
export type InviteDeliveryStatus = (typeof INVITE_DELIVERY_STATUSES)[number];
export const inviteDeliveryStatusSchema = z.enum(INVITE_DELIVERY_STATUSES);

/** `meeting_invite_deliveries.pending_reason` (set exactly while `pending_mail`; D-1021, D-1022). */
export const INVITE_PENDING_REASONS = [
  'mail_off',
  'organiser_cap',
  'self_service_cap',
  'webinar_cap',
  'recipient_cap',
] as const;
export type InvitePendingReason = (typeof INVITE_PENDING_REASONS)[number];
export const invitePendingReasonSchema = z.enum(INVITE_PENDING_REASONS);

/** `meeting_invite_deliveries.suppressed_reason` (set exactly while `suppressed`). */
export const INVITE_SUPPRESSED_REASONS = ['unwanted', 'reserved_domain'] as const;
export type InviteSuppressedReason = (typeof INVITE_SUPPRESSED_REASONS)[number];
export const inviteSuppressedReasonSchema = z.enum(INVITE_SUPPRESSED_REASONS);

/** Why a send failed for good (`failure_reason` of deliveries and notices; D-1020 write-back). */
export const MAIL_FAILURE_REASONS = [
  'smtp_auth',
  'recipient_rejected',
  'timeout',
  'other',
] as const;
export type MailFailureReason = (typeof MAIL_FAILURE_REASONS)[number];
export const mailFailureReasonSchema = z.enum(MAIL_FAILURE_REASONS);

/** `meeting_invite_messages.outcome` (the append-only log; no addresses). */
export const INVITE_MESSAGE_OUTCOMES = ['queued', 'manual_download', 'self_download'] as const;
export type InviteMessageOutcome = (typeof INVITE_MESSAGE_OUTCOMES)[number];
export const inviteMessageOutcomeSchema = z.enum(INVITE_MESSAGE_OUTCOMES);

// ---------------------------------------------------------------- guest notices (mail with no calendar object)

/** `meeting_guest_notices.kind`. */
export const GUEST_NOTICE_KINDS = [
  'registration_confirm',
  'registration_pending',
  'registration_waitlisted',
  'registration_rejected',
  'registration_cancelled',
  'registration_link_resent',
  'webinar_reminder_24h',
  'webinar_reminder_1h',
  'booking_reminder_24h',
  'booking_cancelled_by_host',
  'booking_rescheduled_by_host',
] as const;
export type GuestNoticeKind = (typeof GUEST_NOTICE_KINDS)[number];
export const guestNoticeKindSchema = z.enum(GUEST_NOTICE_KINDS);

/** `meeting_guest_notices.status`. */
export const GUEST_NOTICE_STATUSES = ['sent', 'pending_mail', 'skipped', 'failed'] as const;
export type GuestNoticeStatus = (typeof GUEST_NOTICE_STATUSES)[number];
export const guestNoticeStatusSchema = z.enum(GUEST_NOTICE_STATUSES);

// ---------------------------------------------------------------- the lobby and guest calls (D-1027 ff.)

/** `meeting_lobby_entries.status`. */
export const LOBBY_ENTRY_STATUSES = [
  'waiting',
  'admitted',
  'denied',
  'left',
  'removed',
  'expired',
  'replaced',
] as const;
export type LobbyEntryStatus = (typeof LOBBY_ENTRY_STATUSES)[number];
export const lobbyEntryStatusSchema = z.enum(LOBBY_ENTRY_STATUSES);

/** `meeting_lobby_entries.wait_reason` (set exactly while `waiting`). */
export const LOBBY_WAIT_REASONS = [
  'waiting_for_host',
  'waiting_for_admission',
  'rejoined_from_new_device',
] as const;
export type LobbyWaitReason = (typeof LOBBY_WAIT_REASONS)[number];
export const lobbyWaitReasonSchema = z.enum(LOBBY_WAIT_REASONS);

/** `meeting_lobby_entries.end_reason`. */
export const LOBBY_END_REASONS = ['left', 'meeting_ended', 'expired', 'stale', 'revoked'] as const;
export type LobbyEndReason = (typeof LOBBY_END_REASONS)[number];
export const lobbyEndReasonSchema = z.enum(LOBBY_END_REASONS);

/**
 * LiveKit grant profiles (D-1029): `full` (today's grant), `speaker` (camera and mic, no screen),
 * `listener` (no publish, no data, hidden). `meeting_lobby_entries.profile`, `call_guests.profile`
 * and `LiveKitGrant.profile`.
 */
export const CALL_GRANT_PROFILES = ['full', 'speaker', 'listener'] as const;
export type CallGrantProfile = (typeof CALL_GRANT_PROFILES)[number];
export const callGrantProfileSchema = z.enum(CALL_GRANT_PROFILES);

/** `call_guests.label`: the server-set tag every participant list shows (D-1027). */
export const CALL_GUEST_LABELS = ['external', 'registered', 'presenter'] as const;
export type CallGuestLabel = (typeof CALL_GUEST_LABELS)[number];
export const callGuestLabelSchema = z.enum(CALL_GUEST_LABELS);

/** `call_guests.status`. */
export const CALL_GUEST_STATUSES = ['admitted', 'connected', 'left', 'removed'] as const;
export type CallGuestStatus = (typeof CALL_GUEST_STATUSES)[number];
export const callGuestStatusSchema = z.enum(CALL_GUEST_STATUSES);

/** What kind of identity a LiveKit grant is for (`u_…` member, `g_…` guest; D-1027). */
export const CALL_IDENTITY_KINDS = ['member', 'guest'] as const;
export type CallIdentityKind = (typeof CALL_IDENTITY_KINDS)[number];
export const callIdentityKindSchema = z.enum(CALL_IDENTITY_KINDS);

/** `CallDto.meetingRoom`: the calendar type of a meeting-room call (D-1026), null for every other call. */
export const MEETING_ROOM_KINDS = ['general', 'webinar'] as const;
export type MeetingRoomKind = (typeof MEETING_ROOM_KINDS)[number];
export const meetingRoomKindSchema = z.enum(MEETING_ROOM_KINDS);

// ---------------------------------------------------------------- feeds and preferences (D-1023, D-1042)

/** `calendar_feeds.detail`. */
export const CALENDAR_FEED_DETAILS = ['full', 'busy'] as const;
export type CalendarFeedDetail = (typeof CALENDAR_FEED_DETAILS)[number];
export const calendarFeedDetailSchema = z.enum(CALENDAR_FEED_DETAILS);

/** `calendar_feeds.revoked_reason`. */
export const CALENDAR_FEED_REVOKE_REASONS = [
  'rotated',
  'user_revoked',
  'deactivated',
  'admin_revoked',
  'disabled',
] as const;
export type CalendarFeedRevokeReason = (typeof CALENDAR_FEED_REVOKE_REASONS)[number];
export const calendarFeedRevokeReasonSchema = z.enum(CALENDAR_FEED_REVOKE_REASONS);

/** `calendar_preferences.default_view` (D-1041: Day, Work week, Week, Month, Agenda). */
export const CALENDAR_VIEWS = ['day', 'work_week', 'week', 'month', 'agenda'] as const;
export type CalendarView = (typeof CALENDAR_VIEWS)[number];
export const calendarViewSchema = z.enum(CALENDAR_VIEWS);

/** `calendar_preferences.reminder_minutes` and `calendar.defaultReminderMinutes`. */
export const CALENDAR_REMINDER_MINUTES = [0, 5, 10, 15, 30] as const;
export type CalendarReminderMinutes = (typeof CALENDAR_REMINDER_MINUTES)[number];
export const calendarReminderMinutesSchema = z.union([
  z.literal(0),
  z.literal(5),
  z.literal(10),
  z.literal(15),
  z.literal(30),
]);

/** `calendar.freeBusyLeaveVisibility` (Akshay, 11 Oct 2026: `out_of_office`; D-1024). */
export const FREE_BUSY_LEAVE_VISIBILITIES = ['out_of_office', 'busy'] as const;
export type FreeBusyLeaveVisibility = (typeof FREE_BUSY_LEAVE_VISIBILITIES)[number];
export const freeBusyLeaveVisibilitySchema = z.enum(FREE_BUSY_LEAVE_VISIBILITIES);

/** `calendar.feedPerformanceDetail`: PTSTDM meetings in feeds (`one_on_one` is always busy, D-1023). */
export const FEED_PERFORMANCE_DETAILS = ['busy', 'full'] as const;
export type FeedPerformanceDetail = (typeof FEED_PERFORMANCE_DETAILS)[number];
export const feedPerformanceDetailSchema = z.enum(FEED_PERFORMANCE_DETAILS);

/** A free/busy block (D-1024). */
export const BUSY_KINDS = ['busy', 'tentative', 'out_of_office'] as const;
export type BusyKind = (typeof BUSY_KINDS)[number];
export const busyKindSchema = z.enum(BUSY_KINDS);

/** Why `calendar.suggestTimes` found nothing. */
export const SUGGEST_TIMES_REASONS = ['no_common_time', 'outside_working_hours'] as const;
export type SuggestTimesReason = (typeof SUGGEST_TIMES_REASONS)[number];
export const suggestTimesReasonSchema = z.enum(SUGGEST_TIMES_REASONS);

/**
 * What the UI shows for an occurrence: the stored status, or `ended` for a past calendar-type
 * occurrence that is still `scheduled` or `in_progress` (D-1005; the stored status is unchanged).
 */
export const MEETING_DISPLAY_STATUSES = [...MEETING_OCCURRENCE_STATUSES, 'ended'] as const;
export type MeetingDisplayStatus = (typeof MEETING_DISPLAY_STATUSES)[number];
export const meetingDisplayStatusSchema = z.enum(MEETING_DISPLAY_STATUSES);

// ---------------------------------------------------------------- mail state (D-1020, D-1021)

/** `mailStatus.reason`: SMTP, the dev console outside production, or nothing that sends. */
export const CALENDAR_MAIL_REASONS = ['smtp', 'dev_console', 'not_configured'] as const;
export type CalendarMailReason = (typeof CALENDAR_MAIL_REASONS)[number];
export const calendarMailReasonSchema = z.enum(CALENDAR_MAIL_REASONS);

/**
 * Addresses that are never mailed (RFC 2606 / 6761, D-1021): their deliveries become `suppressed`
 * with `reserved_domain`. The seed uses them for every outside person.
 */
export const RESERVED_MAIL_DOMAINS = ['example.com', 'example.net', 'example.org'] as const;
export const RESERVED_MAIL_SUFFIXES = ['.example', '.test', '.invalid', '.localhost'] as const;

/** True for an address under a reserved domain (case-insensitive). */
export function isReservedMailAddress(email: string): boolean {
  const at = email.lastIndexOf('@');
  if (at < 0) return false;
  const domain = email
    .slice(at + 1)
    .trim()
    .toLowerCase()
    .replace(/\.$/, '');
  if ((RESERVED_MAIL_DOMAINS as readonly string[]).includes(domain)) return true;
  if (RESERVED_MAIL_DOMAINS.some((reserved) => domain.endsWith(`.${reserved}`))) return true;
  return RESERVED_MAIL_SUFFIXES.some(
    (suffix) => domain === suffix.slice(1) || domain.endsWith(suffix),
  );
}

// ---------------------------------------------------------------- DMs (D-1015)

/**
 * `kind` of a `meeting.notify` outbox event: what the system DM says. Invites, changes and
 * cancellations go to the people (never the actor); the rest are notices to organisers or hosts.
 */
export const MEETING_NOTIFY_KINDS = [
  'invited',
  'updated',
  'cancelled',
  'removed',
  'declined',
  'answered',
  'booking_created',
  'booking_cancelled',
  'booking_rescheduled',
  'booking_page_throttled',
  'booking_host_added',
  'registration_pending',
  'webinar_published',
  'invitations_capped',
  'chair_reassigned',
  'reminder',
] as const;
export type MeetingNotifyKind = (typeof MEETING_NOTIFY_KINDS)[number];
export const meetingNotifyKindSchema = z.enum(MEETING_NOTIFY_KINDS);

/** `kind` of a `meeting.guest_activity` event (batched into one organiser DM per 10 minutes). */
export const MEETING_GUEST_ACTIVITY_KINDS = [
  'guest_answered',
  'registration_pending',
  'registration_received',
  'booking_created',
  'booking_changed',
] as const;
export type MeetingGuestActivityKind = (typeof MEETING_GUEST_ACTIVITY_KINDS)[number];
export const meetingGuestActivityKindSchema = z.enum(MEETING_GUEST_ACTIVITY_KINDS);

/** `reason` of a `meeting.guest.revoked` event (D-1049): why live guest sessions end. */
export const GUEST_REVOKE_REASONS = [
  'guest_removed',
  'link_reset',
  'series_cancelled',
  'occurrence_cancelled',
  'guests_disabled',
  'registration_cancelled',
] as const;
export type GuestRevokeReason = (typeof GUEST_REVOKE_REASONS)[number];
export const guestRevokeReasonSchema = z.enum(GUEST_REVOKE_REASONS);

// ---------------------------------------------------------------- webinars (D-1031 ff.)

/** `meeting_webinars.registration_mode`. */
export const WEBINAR_REGISTRATION_MODES = ['public', 'workspace_only'] as const;
export type WebinarRegistrationMode = (typeof WEBINAR_REGISTRATION_MODES)[number];
export const webinarRegistrationModeSchema = z.enum(WEBINAR_REGISTRATION_MODES);

/** `meeting_webinars.page_status`. */
export const WEBINAR_PAGE_STATUSES = ['draft', 'published', 'closed'] as const;
export type WebinarPageStatus = (typeof WEBINAR_PAGE_STATUSES)[number];
export const webinarPageStatusSchema = z.enum(WEBINAR_PAGE_STATUSES);

/** `meeting_webinars.attendees_can_unmute`. */
export const WEBINAR_UNMUTE_POLICIES = ['never', 'when_allowed'] as const;
export type WebinarUnmutePolicy = (typeof WEBINAR_UNMUTE_POLICIES)[number];
export const webinarUnmutePolicySchema = z.enum(WEBINAR_UNMUTE_POLICIES);

/** `webinar_questions.status`. */
export const WEBINAR_QUESTION_STATUSES = ['pending', 'published', 'answered', 'dismissed'] as const;
export type WebinarQuestionStatus = (typeof WEBINAR_QUESTION_STATUSES)[number];
export const webinarQuestionStatusSchema = z.enum(WEBINAR_QUESTION_STATUSES);

/** A registration form question (`meeting_webinars.questions[]`, `booking_pages.questions[]`). */
export const FORM_QUESTION_TYPES = ['text', 'choice', 'yes_no'] as const;
export type FormQuestionType = (typeof FORM_QUESTION_TYPES)[number];
export const formQuestionTypeSchema = z.enum(FORM_QUESTION_TYPES);

/** The two webinar reminders (`webinar-reminders` tick). */
export const WEBINAR_EMAIL_PREVIEW_KINDS = [
  'invitation',
  'update',
  'cancellation',
  'registration_confirm',
  'registration_pending',
  'registration_waitlisted',
  'registration_rejected',
  'registration_cancelled',
  'webinar_reminder_24h',
  'webinar_reminder_1h',
] as const;
export type WebinarEmailPreviewKind = (typeof WEBINAR_EMAIL_PREVIEW_KINDS)[number];
export const webinarEmailPreviewKindSchema = z.enum(WEBINAR_EMAIL_PREVIEW_KINDS);

// ---------------------------------------------------------------- booking pages (D-1035)

/** `booking_pages.kind`. */
export const BOOKING_PAGE_KINDS = ['personal', 'team'] as const;
export type BookingPageKind = (typeof BOOKING_PAGE_KINDS)[number];
export const bookingPageKindSchema = z.enum(BOOKING_PAGE_KINDS);

/** `booking_pages.assignment`. */
export const BOOKING_ASSIGNMENTS = ['round_robin', 'customer_choice'] as const;
export type BookingAssignment = (typeof BOOKING_ASSIGNMENTS)[number];
export const bookingAssignmentSchema = z.enum(BOOKING_ASSIGNMENTS);

/** `booking_pages.location_mode`. */
export const BOOKING_LOCATION_MODES = ['online', 'in_person'] as const;
export type BookingLocationMode = (typeof BOOKING_LOCATION_MODES)[number];
export const bookingLocationModeSchema = z.enum(BOOKING_LOCATION_MODES);

/** `booking_pages.status`. */
export const BOOKING_PAGE_STATUSES = ['active', 'paused', 'archived'] as const;
export type BookingPageStatus = (typeof BOOKING_PAGE_STATUSES)[number];
export const bookingPageStatusSchema = z.enum(BOOKING_PAGE_STATUSES);

/** `booking_pages.paused_reason`. */
export const BOOKING_PAGE_PAUSED_REASONS = ['owner', 'owner_deactivated'] as const;
export type BookingPagePausedReason = (typeof BOOKING_PAGE_PAUSED_REASONS)[number];
export const bookingPagePausedReasonSchema = z.enum(BOOKING_PAGE_PAUSED_REASONS);

/** `bookings.status`: `held` and `confirmed` block the host's time (EXCLUDE `bookings_no_overlap`). */
export const BOOKING_STATUSES = ['held', 'confirmed', 'cancelled', 'expired'] as const;
export type BookingStatus = (typeof BOOKING_STATUSES)[number];
export const bookingStatusSchema = z.enum(BOOKING_STATUSES);

/** `bookings.cancelled_by`. */
export const BOOKING_CANCELLED_BY = ['client', 'host', 'system'] as const;
export type BookingCancelledBy = (typeof BOOKING_CANCELLED_BY)[number];
export const bookingCancelledBySchema = z.enum(BOOKING_CANCELLED_BY);

/** `booking_pages.slot_increment_minutes`. */
export const BOOKING_SLOT_INCREMENTS = [10, 15, 20, 30, 60] as const;
export type BookingSlotIncrement = (typeof BOOKING_SLOT_INCREMENTS)[number];

// ---------------------------------------------------------------- limits

/** Fixed bounds of the wave (the settings of §13.1 have their own bounds in the registry). */
export const CALENDAR_LIMITS = Object.freeze({
  /** `calendar.maxGuestsPerMeeting` default and ceiling (D-1011). */
  maxGuestsPerMeeting: 50,
  /**
   * `webinars.maxAttendees` ceiling (D-1031; raised from 100 to 500 by Alok on 24 Oct 2026,
   * D-1232). This is the ONE definition — it validates `meeting_webinars.capacity` on create and
   * update, the capabilities DTO and the `webinars.maxAttendees` setting, and the database CHECK
   * is generated from it. `CALL_LIMITS.webinarMaxParticipants` is this plus the presenters.
   */
  webinarMaxAttendees: 500,
  /** Presenters per webinar, of whom at most `webinarMaxExternalPresenters` are outside guests. */
  webinarMaxPresenters: 10,
  webinarMaxExternalPresenters: 5,
  /** Registrations of any status per webinar: 2 × capacity (D-1032). */
  webinarRegistrationFactor: 2,
  /** Custom questions on a registration form and on a booking page. */
  webinarMaxQuestions: 10,
  bookingMaxQuestions: 5,
  /** `listEvents` range and truncation (§6.2); free/busy people and days. */
  eventsMaxRangeDays: 62,
  eventsMaxItems: 2000,
  freeBusyMaxPeople: 30,
  freeBusyMaxDays: 31,
  suggestTimesMax: 3,
  /** `POST /meeting-series/:id/occurrences` and `GET /meeting-occurrences` reach (D-1007). */
  materialiseMaxDaysAhead: 400,
  /** Public slots window (§6.7). */
  bookingSlotsMaxDays: 14,
  /** Feed window and cache (D-1023). */
  feedDaysBack: 30,
  feedDaysAhead: 180,
  feedCacheSeconds: 300,
  feedMaxBytes: 1_048_576,
  /** `last_digest` and `ical` content (D-1018, D-1020). */
  deliveryDigestMaxBytes: 65_536,
  icsMaxBytes: 262_144,
  /** Unconfirmed registrations and held bookings last 15 minutes (D-1032, D-1035). */
  confirmHoldMinutes: 15,
  /** Form tickets: accepted 3 s to 2 h after the page GET, at most 5 uses (D-1036). */
  formTicketMinAgeSeconds: 3,
  formTicketMaxAgeSeconds: 7200,
  formTicketMaxUses: 5,
  /** A denied guest may ask again this many times per occurrence (D-1028). */
  lobbyDenyLimit: 3,
  /** Names from outside people are capped (D-1037). */
  outsideNameMax: 80,
  /** Join window around an occurrence (§8). */
  joinWindowBeforeMinutes: 15,
  joinWindowAfterMinutes: 30,
} as const);
